import { ApolloClient, InMemoryCache, createHttpLink, from, Observable } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { auth, getIdToken } from './firebase'
import { GRAPHQL_URL } from './env'

const httpLink = createHttpLink({
  uri: GRAPHQL_URL,
})

const authLink = setContext(async (_, { headers }) => {
  const user = auth.currentUser
  if (!user) return { headers }

  const token = await getIdToken(user, false)
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  }
})

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      // Auto-refresh token and retry on auth errors
      if (
        err.extensions?.code === 'UNAUTHENTICATED' ||
        err.message.includes('401') ||
        err.message.toLowerCase().includes('unauthorized')
      ) {
        const user = auth.currentUser
        if (user) {
          return new Observable((observer) => {
            getIdToken(user, true)
              .then((newToken) => {
                operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
                  headers: {
                    ...headers,
                    authorization: newToken ? `Bearer ${newToken}` : '',
                  },
                }))
              })
              .then(() => {
                const subscriber = forward(operation).subscribe({
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                })
                return () => subscriber.unsubscribe()
              })
              .catch((refreshError) => {
                observer.error(refreshError)
              })
          })
        }
      }
      console.error(`[GraphQL error]: ${err.message}`)
    }
  }
  if (networkError) {
    // Retry on 401 network errors
    if ('statusCode' in networkError && (networkError as { statusCode: number }).statusCode === 401) {
      const user = auth.currentUser
      if (user) {
        return new Observable((observer) => {
          getIdToken(user, true)
            .then((newToken) => {
              operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
                headers: {
                  ...headers,
                  authorization: newToken ? `Bearer ${newToken}` : '',
                },
              }))
            })
            .then(() => {
              const subscriber = forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              })
              return () => subscriber.unsubscribe()
            })
            .catch((refreshError) => {
              observer.error(refreshError)
            })
        })
      }
    }
    console.error(`[Network error]: ${networkError}`)
  }
})

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
})
